import { createPkce, pkce } from "./pkce";
import { jwtDecode } from "jwt-decode";

export type LoginDetails = {
        accessToken: string,
    refreshToken: string,
    environment: string,
    name: string,
    email: string
}

export async function login(environment: string): Promise<LoginDetails|null> {

    const pkce = await createPkce();
    const authUrl =
        `${environment}cms/oauth/authorization` +
        `?response_type=code` +
        `&client_id=excel` +
        `&scope=openid profile email offline_access tablesync` +
        `&redirect_uri=https://${document.location.host}/auth/callback.html` +
        `&code_challenge=${pkce.codeChallenge}` +
        `&code_challenge_method=S256`;

    const redirectUrl = 
        `https://${document.location.host}/auth/redirect.html?redirect=${encodeURIComponent(authUrl)}`

    return new Promise((resolve, reject) => {
        Office.context.ui.displayDialogAsync(
            redirectUrl, 
            { height: 65, width: 40 },
            async (result) => {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                    reject("Failed to open dialog");
                    return;
                }
                const dialog = result.value;
                dialog.addEventHandler(
                    Office.EventType.DialogMessageReceived,
                    async arg => {
                        if ("error" in arg) {
                            reject("DialogMessageReceived error " + arg.error);
                        } else {
                            const data = JSON.parse(arg.message);
                            if (data.code) {
                                dialog.close();
                                const loginDetails = await exchangeCodeForToken(environment, data.code, pkce.codeVerifier);
                                resolve(loginDetails);
                            } else {
                                reject("Missing code");
                                return;
                            }
                        }
                    
                    }
                );
                dialog.addEventHandler(
                    Office.EventType.DialogEventReceived,
                    arg => {
                        if ("error" in arg && arg.error === 12006) {
                            reject("Dialog closed");
                            return;
                        }
                    }
                );
            }
        );
    });
}

export async function refresh(environment: string, refreshToken: string) {
    try {
        const response = await fetch(`${environment}cms/login/oauth/token`, {
                method: "POST",
                // signal: AbortSignal.timeout(5000),
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({
                    client_id: "excel",
                    grant_type: "refresh_token",
                    refresh_token: refreshToken,
                })
            }
        );
        const tokens = await response.json();
        if ("error" in tokens) {
            console.error(tokens.error, tokens.error_description, tokens.error_uri);
            return null;
        }
        const ret = <LoginDetails>{
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            environment: environment,
        };
        return ret;
    } catch (err: any) {
        console.error(err, err.name, err.message);
        return null;
    }
}

async function exchangeCodeForToken(environment: string, authorizationCode: string, codeVerifier: string): Promise<LoginDetails|null> {

    try {
        const response = await fetch(`${environment}cms/login/oauth/token`, {
                method: "POST",
                // signal: AbortSignal.timeout(5000),
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({
                    client_id: "excel",
                    grant_type: "authorization_code",
                    code: authorizationCode,
                    code_verifier: codeVerifier,
                    redirect_uri: `https://${document.location.host}/auth/callback.html`
                })
            }
        );
        const tokens = await response.json();
        if ("error" in tokens) {
            console.error(tokens.error, tokens.error_description, tokens.error_uri);
            return null;
        }
        
        const identity = <any>jwtDecode(tokens.id_token);
        const ret = <LoginDetails>{
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            environment: environment,
            email: identity.email, 
            name: identity.name
        };
        return ret;
    } catch (err: any) {
        console.error(err, err.name, err.message);
        return null;
    }
}